import { api } from '@/api/apiClient';

/**
 * Create a notification for a user
 */
export async function createNotification({
  userId,
  title,
  message,
  type = 'info',
  category = 'system',
  actionUrl = null,
  actionLabel = null,
  relatedEntityType = null,
  relatedEntityId = null,
  priority = 'medium',
  expiresInDays = null
}) {
  try {
    const notificationData = {
      user_id: userId,
      title,
      message,
      type,
      category,
      priority,
      is_read: false
    };

    if (actionUrl) notificationData.action_url = actionUrl;
    if (actionLabel) notificationData.action_label = actionLabel;
    if (relatedEntityType) notificationData.related_entity_type = relatedEntityType;
    if (relatedEntityId) notificationData.related_entity_id = relatedEntityId;
    
    if (expiresInDays) {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expiresInDays);
      notificationData.expires_at = expiresAt.toISOString();
    }

    await api.asServiceRole.entities.Notification.create(notificationData);
  } catch (err) {
    console.error('Failed to create notification:', err);
  }
}

/**
 * Notify about new employee enrollment
 */
export async function notifyNewEnrollment(employeeEmail, enrollmentId, productName, hrEmail) {
  await createNotification({
    userId: employeeEmail,
    title: 'âœ… Enrollment Confirmed',
    message: `Your enrollment in ${productName} has been confirmed and is pending approval.`,
    type: 'success',
    category: 'enrollment',
    actionUrl: 'MyBenefits',
    actionLabel: 'View Benefits',
    relatedEntityType: 'Enrollment',
    relatedEntityId: enrollmentId,
    priority: 'medium'
  });

  // Notify HR
  if (hrEmail) {
    await createNotification({
      userId: hrEmail,
      title: 'ðŸ“‹ New Enrollment Pending',
      message: `New enrollment for ${productName} requires approval.`,
      type: 'action_required',
      category: 'approval',
      actionUrl: 'EmployerEnrollments',
      actionLabel: 'Review Enrollments',
      relatedEntityType: 'Enrollment',
      relatedEntityId: enrollmentId,
      priority: 'high'
    });
  }
}

/**
 * Notify about enrollment approval
 */
export async function notifyEnrollmentApproval(employeeEmail, productName, enrollmentId) {
  await createNotification({
    userId: employeeEmail,
    title: 'ðŸŽ‰ Enrollment Approved',
    message: `Your ${productName} enrollment has been approved and is now active!`,
    type: 'success',
    category: 'enrollment',
    actionUrl: 'MyBenefits',
    actionLabel: 'View Benefits',
    relatedEntityType: 'Enrollment',
    relatedEntityId: enrollmentId,
    priority: 'high'
  });
}

/**
 * Notify about claim submission
 */
export async function notifyClaimSubmitted(employeeEmail, claimId, amount) {
  await createNotification({
    userId: employeeEmail,
    title: 'ðŸ“ Claim Submitted',
    message: `Your claim for $${amount} has been submitted and is under review.`,
    type: 'info',
    category: 'claim',
    actionUrl: 'Claims',
    actionLabel: 'View Claim',
    relatedEntityType: 'Claim',
    relatedEntityId: claimId,
    priority: 'medium'
  });
}

/**
 * Notify about claim approval
 */
export async function notifyClaimApproved(employeeEmail, claimId, amount) {
  await createNotification({
    userId: employeeEmail,
    title: 'ðŸ’° Claim Approved',
    message: `Your claim for $${amount} has been approved! Payment will be processed soon.`,
    type: 'success',
    category: 'claim',
    actionUrl: 'Claims',
    actionLabel: 'View Claim',
    relatedEntityType: 'Claim',
    relatedEntityId: claimId,
    priority: 'high'
  });
}

/**
 * Notify about QLE submission
 */
export async function notifyQLESubmitted(employeeEmail, qleId, eventType, hrEmail) {
  await createNotification({
    userId: employeeEmail,
    title: 'ðŸ“… Qualifying Life Event Submitted',
    message: `Your ${eventType} event has been submitted for review.`,
    type: 'info',
    category: 'qle',
    actionUrl: 'QualifyingLifeEvents',
    actionLabel: 'View Status',
    relatedEntityType: 'QualifyingLifeEvent',
    relatedEntityId: qleId,
    priority: 'medium'
  });

  // Notify HR
  if (hrEmail) {
    await createNotification({
      userId: hrEmail,
      title: 'ðŸ“‹ QLE Pending Review',
      message: `Qualifying life event (${eventType}) requires review.`,
      type: 'action_required',
      category: 'approval',
      actionUrl: 'QualifyingLifeEvents',
      actionLabel: 'Review QLE',
      relatedEntityType: 'QualifyingLifeEvent',
      relatedEntityId: qleId,
      priority: 'high'
    });
  }
}

/**
 * Notify about QLE approval
 */
export async function notifyQLEApproved(employeeEmail, qleId, eventType, enrollmentEndDate) {
  await createNotification({
    userId: employeeEmail,
    title: 'âœ… Qualifying Life Event Approved',
    message: `Your ${eventType} has been approved. You can now make benefit changes until ${enrollmentEndDate}.`,
    type: 'success',
    category: 'qle',
    actionUrl: 'BenefitsCatalog',
    actionLabel: 'Update Benefits',
    relatedEntityType: 'QualifyingLifeEvent',
    relatedEntityId: qleId,
    priority: 'urgent',
    expiresInDays: 60
  });
}

/**
 * Notify about enrollment deadline
 */
export async function notifyEnrollmentDeadline(employeeEmail, daysRemaining, deadlineDate) {
  await createNotification({
    userId: employeeEmail,
    title: 'â° Enrollment Deadline Approaching',
    message: `You have ${daysRemaining} days left to complete your benefits enrollment (deadline: ${deadlineDate}).`,
    type: 'warning',
    category: 'reminder',
    actionUrl: 'BenefitsCatalog',
    actionLabel: 'Complete Enrollment',
    priority: 'urgent',
    expiresInDays: daysRemaining
  });
}

/**
 * Notify about missing documents
 */
export async function notifyMissingDocuments(employeeEmail, documentType, relatedId) {
  await createNotification({
    userId: employeeEmail,
    title: 'âš ï¸ Documents Required',
    message: `Please upload required ${documentType} to complete your enrollment.`,
    type: 'warning',
    category: 'document',
    actionUrl: 'FamilyMembers',
    actionLabel: 'Upload Documents',
    relatedEntityType: 'FamilyMember',
    relatedEntityId: relatedId,
    priority: 'high'
  });
}

/**
 * Notify employee invitation
 */
export async function notifyEmployeeInvitation(employeeEmail, employerName, inviteCode) {
  await createNotification({
    userId: employeeEmail,
    title: 'ðŸŽ‰ Welcome to Benefits Portal',
    message: `${employerName} has invited you to enroll in benefits. Your invitation code: ${inviteCode}`,
    type: 'action_required',
    category: 'enrollment',
    actionUrl: 'InviteCodeEntry',
    actionLabel: 'Get Started',
    priority: 'urgent',
    expiresInDays: 30
  });
}

/**
 * Mark notification as read
 */
export async function markNotificationRead(notificationId) {
  try {
    await api.entities.Notification.update(notificationId, { is_read: true });
  } catch (err) {
    console.error('Failed to mark notification as read:', err);
  }
}

/**
 * Mark all notifications as read for a user
 */
export async function markAllNotificationsRead(userId) {
  try {
    const notifications = await api.entities.Notification.filter({
      user_id: userId,
      is_read: false
    });
    
    await Promise.all(
      notifications.map(n => api.entities.Notification.update(n.id, { is_read: true }))
    );
  } catch (err) {
    console.error('Failed to mark all notifications as read:', err);
  }
}

/**
 * Delete notification
 */
export async function deleteNotification(notificationId) {
  try {
    await api.entities.Notification.delete(notificationId);
  } catch (err) {
    console.error('Failed to delete notification:', err);
  }
}
